import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, Certificate } from '@/api/admin';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { formatDistanceToNow, format } from 'date-fns';
import { Shield, RefreshCw, AlertTriangle, CheckCircle, XCircle, Clock, FileText } from 'lucide-react';

export function AdminCertificatesPage() {
  const [selectedCert, setSelectedCert] = useState<Certificate | null>(null);
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-certificates'],
    queryFn: () => adminApi.getCertificates(),
    refetchInterval: 60000,
  });

  const { data: logs, isLoading: logsLoading } = useQuery({
    queryKey: ['admin-certificate-logs', selectedCert?.id],
    queryFn: () => adminApi.getCertificateLogs(selectedCert!.id),
    enabled: !!selectedCert && logsModalOpen,
  });

  const renewMutation = useMutation({
    mutationFn: (id: string) => adminApi.renewCertificate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-certificates'] });
    },
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Active':
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded">
            <CheckCircle className="w-3 h-3" /> Active
          </span>
        );
      case 'ExpiringSoon':
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 rounded">
            <Clock className="w-3 h-3" /> Expiring Soon
          </span>
        );
      case 'Failed':
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded">
            <XCircle className="w-3 h-3" /> Failed
          </span>
        );
      case 'Expired':
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded">
            <AlertTriangle className="w-3 h-3" /> Expired
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 rounded">
            {status}
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">SSL Certificates</h1>
          <p className="text-gray-500 dark:text-gray-400">Manage SSL certificates for custom domains</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{data?.summary.total || 0}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Total</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{data?.summary.active || 0}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Active</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-yellow-600">{data?.summary.expiringSoon || 0}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Expiring Soon</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{data?.summary.failed || 0}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Failed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{data?.summary.expired || 0}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Expired</p>
          </CardContent>
        </Card>
      </div>

      {/* Certificate List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Certificates
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : data?.certificates.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No certificates found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Domain</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Project</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">User</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Type</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Expires</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.certificates.map((cert) => (
                    <tr key={cert.id} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="py-3 px-4">
                        <p className="font-medium text-gray-900 dark:text-white">{cert.domain}</p>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">
                        {cert.projectName}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500 dark:text-gray-400">
                        {cert.userEmail}
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 rounded">
                          {cert.sslType}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {getStatusBadge(cert.status)}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500 dark:text-gray-400">
                        {cert.expiresAt ? (
                          <>
                            <p>{format(new Date(cert.expiresAt), 'MMM d, yyyy')}</p>
                            <p className="text-xs">{formatDistanceToNow(new Date(cert.expiresAt), { addSuffix: true })}</p>
                          </>
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedCert(cert);
                              setLogsModalOpen(true);
                            }}
                            title="View logs"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => renewMutation.mutate(cert.id)}
                            disabled={renewMutation.isPending}
                            title="Force renewal"
                          >
                            <RefreshCw className={`w-4 h-4 ${renewMutation.isPending ? 'animate-spin' : ''}`} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Logs Modal */}
      <Modal
        isOpen={logsModalOpen}
        onClose={() => {
          setLogsModalOpen(false);
          setSelectedCert(null);
        }}
        title={`Renewal Logs - ${selectedCert?.domain}`}
      >
        {logsLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          </div>
        ) : logs?.length === 0 ? (
          <p className="text-center py-8 text-gray-500 dark:text-gray-400">No renewal logs found</p>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {logs?.map((log) => (
              <div
                key={log.id}
                className={`p-3 rounded-lg border ${
                  log.success
                    ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                    : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    {log.success ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-600" />
                    )}
                    <span className={`text-sm font-medium ${log.success ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                      {log.success ? 'Success' : 'Failed'}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {format(new Date(log.attemptedAt), 'MMM d, yyyy HH:mm')}
                  </span>
                </div>
                {log.error && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">{log.error}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
